import {
  useWSModalCustom,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Batch } from "@wingspanhq/wingspan-api-sdk";
import { queriesBatches } from "@query";

import { ErrorAlert } from "@components/ErrorAlert";

type FormData = {
  name: string;
  notes: string;
};

type Props = {
  batch: Batch;
} & WSModalCustomProps<boolean>;

const ModalEditImportBatchName: React.FC<Props> = props => {
  const batchId = props.batch.id as string;
  const [updateBatch, updateBatchMeta] =
    queriesBatches.updateBatch.useMutation();

  const currentName = props.batch.name ?? "";
  const currentNotes = props.batch.notes ?? "";

  const handleSubmit = async (data: FormData) => {
    await updateBatch(
      { batchId, batchUpdate: { name: data.name, notes: data.notes } },
      { onSuccess: () => props.onClose(true) }
    );
  };

  return (
    <WSForm<FormData>
      defaultValues={{ name: currentName, notes: currentNotes }}
      onSubmit={handleSubmit}
    >
      <WSForm.Values names={["name", "notes"]}>
        {({ name, notes }) => (
          <WSModal
            title="Edit name"
            size="S"
            onClose={() => props.onClose(false)}
            actions={{
              alignment: "fill",
              buttons: [
                {
                  kind: "Tertiary",
                  label: "Cancel",
                  type: "button",
                  onClick: () => props.onClose(false)
                },
                {
                  label: "Save changes",
                  kind: "Primary",
                  disabled:
                    (!name && !notes) ||
                    (name === currentName && notes === currentNotes),
                  loading: updateBatchMeta.isLoading
                }
              ]
            }}
          >
            <WSList gap="XL">
              <WSForm.Field
                name="name"
                label="Batch name"
                component={WSInputText}
                componentProps={{
                  placeholder: "Enter name for the import batch"
                }}
              />
              <WSForm.Field
                name="notes"
                label="Notes"
                component={WSInputTextarea}
                componentProps={{
                  placeholder: "Provide notes for your reference",
                  helperText:
                    "Provide details about the batch for your reference"
                }}
              />

              <WSInfoBox>
                <WSText.ParagraphSm weight="book" color="gray600">
                  Tip: adding a name and notes can be helpful when filtering by
                  batches in the contractor list and help identify the purpose
                  of an imported batch for your own reference.
                </WSText.ParagraphSm>
              </WSInfoBox>

              <ErrorAlert
                context="BulkBatchUpdate"
                error={updateBatchMeta.error}
              />
            </WSList>
          </WSModal>
        )}
      </WSForm.Values>
    </WSForm>
  );
};

export const useModalEditImportBatchName = () =>
  useWSModalCustom(ModalEditImportBatchName);
